package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsSavedController
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.GemType
import diana.soleil.design.iraniancanadienbazzar.core.model.Recipe
import diana.soleil.design.iraniancanadienbazzar.core.model.SavedGem
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

data class RecipeDetailUiState(
    val loading: Boolean = true,
    val recipe: Recipe? = null,
    val liked: Boolean = false,
    val saved: Boolean = false,
    /** Guest tried to like — the route sends them to sign-in. */
    val signInRequired: Boolean = false,
    val reportStatus: GemReportStatus = GemReportStatus.Idle,
)

/**
 * One recipe for the detail + cook screens (one-shot read — deep links land here without the
 * browser's stream). Like/save state rides the live per-user streams so it survives navigation.
 */
class RecipeDetailViewModel(
    private val gemsRepository: GemsRepository,
    private val savedController: GemsSavedController,
    private val reportRepository: ReportRepository,
    private val recipeId: String,
) : ViewModel() {

    private val recipeFlow = MutableStateFlow<Recipe?>(null)
    private val loading = MutableStateFlow(true)
    private val signInGate = MutableStateFlow(false)
    private val reportStatus = MutableStateFlow(GemReportStatus.Idle)

    init {
        viewModelScope.launch {
            recipeFlow.value = (gemsRepository.getRecipe(recipeId) as? DataResult.Success)?.data
            loading.value = false
        }
    }

    val state: StateFlow<RecipeDetailUiState> =
        combine(
            recipeFlow,
            loading,
            combine(gemsRepository.observeLikedRecipeIds(), savedController.saved) { liked, saved ->
                liked to saved
            },
            combine(signInGate, reportStatus) { gate, report -> gate to report },
        ) { recipe, isLoading, (likedIds, savedGems), (gate, report) ->
            RecipeDetailUiState(
                loading = isLoading,
                recipe = recipe,
                liked = recipeId in likedIds,
                saved = savedGems.any { it.type == GemType.Recipe && it.id == recipeId },
                signInRequired = gate,
                reportStatus = report,
            )
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), RecipeDetailUiState())

    fun toggleLike() {
        viewModelScope.launch {
            val result = gemsRepository.setRecipeLiked(recipeId, !state.value.liked)
            if (result is DataResult.Failure && result.error is AppError.Unauthorized) {
                signInGate.value = true
            }
        }
    }

    fun toggleSave() {
        viewModelScope.launch { savedController.toggle(SavedGem(id = recipeId, type = GemType.Recipe)) }
    }

    fun consumeSignInGate() {
        signInGate.value = false
    }

    fun report(reason: String) {
        if (reportStatus.value == GemReportStatus.Sending) return
        viewModelScope.launch {
            reportStatus.value = GemReportStatus.Sending
            reportStatus.value = when (val result = reportRepository.fileReport("recipe", recipeId, reason)) {
                is DataResult.Success -> GemReportStatus.Sent
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) GemReportStatus.SignInRequired
                    else GemReportStatus.Failed
            }
        }
    }

    fun resetReport() {
        reportStatus.value = GemReportStatus.Idle
    }
}
